"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GaugemeisterWrapper = void 0;
const solana_contrib_1 = require("@saberhq/solana-contrib");
const web3_js_1 = require("@solana/web3.js");
const Realm_1 = require("../Realm");
const _1 = require(".");
const bn_js_1 = __importDefault(require("bn.js"));
class GaugemeisterWrapper {
    constructor(sdk, address) {
        this.sdk = sdk;
        this.address = address;
        this._data = null;
        this._realm = null;
    }
    get provider() {
        return this.sdk.provider;
    }
    get program() {
        return this.sdk.program;
    }
    async reload() {
        this._data = await this.program.account.gaugemeister.fetch(this.address);
        if (!this._realm || !this._realm.address.equals(this._data.realm)) {
            this._realm = new Realm_1.RealmWrapper(this.sdk, this._data.realm);
        }
        return this._data;
    }
    async tryReload() {
        this._data = await this.program.account.gaugemeister.fetchNullable(this.address);
        if (this._data &&
            (!this._realm || !this._realm.address.equals(this._data.realm))) {
            this._realm = new Realm_1.RealmWrapper(this.sdk, this._data.realm);
        }
        return this._data;
    }
    async data() {
        if (!this._data) {
            return await this.reload();
        }
        return this._data;
    }
    async tryData() {
        if (!this._data) {
            return await this.tryReload();
        }
        return this._data;
    }
    async realm() {
        if (!this._realm) {
            await this.reload();
        }
        return this._realm;
    }
    async admin() {
        return (await (await this.realm()).data()).admin;
    }
    async createAccount(accountKP, rentPayer) {
        const signers = [accountKP];
        if (rentPayer) {
            signers.push(rentPayer);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            web3_js_1.SystemProgram.createAccount({
                fromPubkey: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || this.provider.wallet.publicKey,
                newAccountPubkey: this.address,
                lamports: await this.provider.connection.getMinimumBalanceForRentExemption(GaugemeisterWrapper.SPACE),
                space: GaugemeisterWrapper.SPACE,
                programId: this.program.programId,
            }),
        ], signers);
    }
    async init({ realm, address, foreman = this.provider.wallet.publicKey, admin, rentPayer, }) {
        let tx = new solana_contrib_1.TransactionEnvelope(this.provider, []);
        if (address) {
            if (!address.publicKey.equals(this.address)) {
                throw new Error(`Expected account key to be ${this.address.toBase58()}`);
            }
            tx = tx.combine(await this.createAccount(address, rentPayer));
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        return tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .initGaugemeister(foreman)
                .accounts({
                gaugemeister: this.address,
                realm: realm.address,
                admin,
            })
                .instruction(),
        ], signers));
    }
    static async create({ realm, address = new web3_js_1.Keypair(), foreman = realm.provider.wallet.publicKey, admin, rentPayer, }) {
        const wrapper = new GaugemeisterWrapper(realm.sdk, address.publicKey);
        const tx = await wrapper.init({
            realm,
            address,
            foreman,
            admin,
            rentPayer,
        });
        return {
            tx,
            wrapper,
        };
    }
    async setForeman({ newForeman, foreman, }) {
        if (!foreman) {
            foreman = (await this.data()).foreman;
        }
        const signers = [];
        if (!(foreman instanceof web3_js_1.PublicKey)) {
            signers.push(foreman);
            foreman = foreman.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .setForeman(newForeman)
                .accounts({
                gaugemeister: this.address,
                foreman,
            })
                .instruction(),
        ], signers);
    }
    async configSnapshot({ foreman, newTime = new bn_js_1.default('9223372036854775807'), newSlot = new bn_js_1.default('ffffffffffffffff', 'hex'), }) {
        if (!foreman) {
            foreman = (await this.data()).foreman;
        }
        const signers = [];
        if (!(foreman instanceof web3_js_1.PublicKey)) {
            signers.push(foreman);
            foreman = foreman.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .configGaugemeisterSnapshot(newTime, newSlot)
                .accounts({
                gaugemeister: this.address,
                foreman,
            })
                .instruction(),
        ], signers);
    }
    async close({ foreman, rentCollector, realm, }) {
        if (!foreman) {
            foreman = (await this.data()).foreman;
        }
        const signers = [];
        if (!(foreman instanceof web3_js_1.PublicKey)) {
            signers.push(foreman);
            foreman = foreman.publicKey;
        }
        if (!realm) {
            realm = (await this.data()).realm;
        }
        if (!(realm instanceof web3_js_1.PublicKey)) {
            realm = realm.address;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .closeGaugemeister()
                .accounts({
                gaugemeister: this.address,
                realm,
                foreman,
                rentCollector: rentCollector || this.provider.wallet.publicKey,
            })
                .instruction(),
        ], signers);
    }
    loadGauges() {
        return _1.GaugeWrapper.loadByGaugemaister(this);
    }
    static async loadByRealm(realm) {
        const gaugemeisters = await realm.program.account.gaugemeister.all(realm.address.toBuffer());
        return gaugemeisters.map(gaugemeister => {
            const wrapper = new GaugemeisterWrapper(realm.sdk, gaugemeister.publicKey);
            wrapper._data = gaugemeister.account;
            return wrapper;
        });
    }
}
exports.GaugemeisterWrapper = GaugemeisterWrapper;
GaugemeisterWrapper.SPACE = 120 + 8 * 32;
//# sourceMappingURL=Gaugemeister.js.map